import React from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { Section } from '@wix/da-a11y/pkg/components/headings/tagWrappers';
import Heading from '@wix/da-a11y/pkg/components/headings/Heading';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks/useFlag';
import Timestamp from '../../../Timestamp';
import ExcerptRenderer from '../../../TextRenderer/ExcerptRenderer';
import { useDeviationCoverImage } from '../../hooks/useDeviationCoverImage';
import {
  styleFromWidthHeight,
  TextDumboType,
  getTextDumboTypeBySize,
  getDeviationTitle,
} from '../../_helpers';
import { DumboProps as Props } from '../types';

import s from './Journal.scss';

const JournalDumbView: React.FC<Props> = ({
  deviation,
  className,
  useTextlessMode,
  width,
  height,
  responsive = true,
  'aria-hidden': ariaHidden,
}) => {
  const { t } = useTranslation();

  // we need to show image background on full height for Micro and Nano thumbs
  const type = getTextDumboTypeBySize(height || width);
  const isMicroOrNano = [TextDumboType.MICRO, TextDumboType.NANO].includes(
    type
  );
  const isWayTooTinyForAnySense = type === TextDumboType.ANTFART;

  // if this is being used to show an editor-embedded journal,
  // it may have a frozen version of the deviation which may include the
  // older `coverImage` field we used to store cover images before `media`
  const cover_attachment = useFlag('cover_attachment');
  const deviationOrFallbackCoverImage =
    deviation.media && cover_attachment ? deviation : deviation.coverImage;
  const { asBackgroundStyleProp } = useDeviationCoverImage({
    deviation: deviationOrFallbackCoverImage,
    width,
    height,
  });
  const style = styleFromWidthHeight({ width, height }, responsive);
  const rootStyle =
    isMicroOrNano && asBackgroundStyleProp
      ? { ...style, ...asBackgroundStyleProp }
      : style;
  const headerAttributes =
    !isMicroOrNano && !isWayTooTinyForAnySense
      ? { style: asBackgroundStyleProp }
      : {};

  return (
    <Section
      className={classnames(
        s['root'],
        s[type],
        useTextlessMode && s['default-bg'],
        className
      )}
      style={rootStyle}
      aria-hidden={ariaHidden}
      data-type={type}
    >
      {!useTextlessMode && (
        <>
          <div className={s['header']} {...headerAttributes}>
            <Heading className={s['title']}>
              {!deviation.isPublished && (
                <div className={s['draft']}>{t('writer.status.draft')}</div>
              )}
              {getDeviationTitle(deviation)}
            </Heading>
          </div>
          <div className={s['body']}>
            <div className={s['date']}>
              <Timestamp timestamp={deviation.publishedTime} />
            </div>
            <ExcerptRenderer
              className={s['text']}
              textContent={deviation.textContent}
              htmlExcerptLegacy
            />
          </div>
        </>
      )}
    </Section>
  );
};
JournalDumbView.displayName = 'JournalDumbView';

export default JournalDumbView;
